import {
  SalesOrderDto,
  SalesOrderListDto,
  SalesOrderStatusDto,
  SalesOrderStatusGroupDto,
  SalesOrderStatusHistoryDto
} from 'src/models/salesorder';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  getSalesOrderById,
  getSalesOrderStatuses,
  getSalesOrderStatusGroup,
  getSalesOrderStatusHistory
} from 'src/services/salesorder';
import { MessagingServiceDto } from 'src/models/messaging';
import { AttachmentDto, AttachmentTypeDto, TrackingAttachmentDto } from 'src/models/attachment';
import { getAttachments, getAttachmentTypes, getTrackingAttachments } from 'src/services/attachment';
import { QueryProps } from 'src/queries/baseQueryFunctions';
import { Products } from 'src/@core/utils/getProductLevel';

export const ENTITY_TYPE_ID = '8AF48D21-1212-4DF6-BE73-32D1CF7C5988';
export const STATUS_IMAGE_ATTACHMENT_TYPE_ID = '9EBF6926-886D-4E68-A4C3-91DDC096929E';
export const PROOF_OF_DELIVERY_ATTACHMENT_TYPE_ID = '54112775-afba-4a4b-9a3e-999d988cf929';

export interface OrderStatusPanelData {
  attachmentTypes?: AttachmentTypeDto[];
  messagingService?: MessagingServiceDto;
  salesOrder?: SalesOrderDto;
  statusHistory?: SalesOrderStatusHistoryDto[];
  statusGroups?: SalesOrderStatusGroupDto[];
  attachments?: AttachmentDto[];
  attachmentsOverride?: TrackingAttachmentDto[];
  allStatuses?: SalesOrderStatusDto[];
}

const orderStatusPanelQueryFn = async (
  orderId: string,
  partnerId: string,
  isDeleted: boolean,
  useOverride: boolean,
  orderIdHash: string | undefined,
  prod: Products | 0,
  salesOrderSummary?: Pick<SalesOrderListDto, 'deliveryCountryCode' | 'orderNumber'>
): Promise<OrderStatusPanelData> => {
  if (!useOverride) {
    const attachmentTypes = (await getAttachmentTypes()).data;
    const statusImageAttachmentType = attachmentTypes.find(x => x.isStatusImage);
    const proofOfDeliveryAttachmentType = attachmentTypes.find(x => x.isPod);

    let statusGroups: SalesOrderStatusGroupDto[] = [];
    let allStatuses: SalesOrderStatusDto[] = [];
    if (prod === Products.MyPortal) {
      statusGroups = (await getSalesOrderStatusGroup({ sort: 'percentage', filter: `isActive eq true` })).data;
      allStatuses = (await getSalesOrderStatuses()).data;
    }

    const salesOrder = salesOrderSummary
      ? Promise.resolve(undefined)
      : getSalesOrderById(orderId, {
          showDeleted: isDeleted
        });
    const statusHistory = getSalesOrderStatusHistory(
      {
        filter: `salesOrderId eq guid'${orderId}'`,
        showDeleted: isDeleted
      },
      partnerId
    );
    const attachments = getAttachments({
      filter: `entityId eq guid'${orderId}' and (attachmentTypeId eq guid'${
        statusImageAttachmentType?.id ?? ''
      }' or attachmentTypeId eq guid'${
        proofOfDeliveryAttachmentType?.id ?? ''
      }') and entityTypeId eq guid'${ENTITY_TYPE_ID}'`
    });

    const res = await Promise.all([salesOrder, statusHistory, attachments]);

    return {
      attachments: res[2].data,
      attachmentTypes: attachmentTypes,
      salesOrder: res[0]?.data,
      statusGroups: statusGroups,
      statusHistory: res[1].data,
      allStatuses: allStatuses
    };
  } else {
    const attachments = (await getTrackingAttachments(orderId, { Hash: orderIdHash ?? '' })).data;

    return {
      attachmentsOverride: attachments
    };
  }
};

export const useOrderStatusPanelQuery = (
  orderId: string,
  partnerId: string,
  isDeleted: boolean,
  useOverride: boolean,
  orderIdHash: string | undefined,
  prod: Products | 0,
  {
    enabled = true,
    initialData = {},
    onSuccess = undefined,
    refetchOnWindowFocus = false
  }: QueryProps<OrderStatusPanelData, {}>,
  salesOrderSummary?: Pick<SalesOrderListDto, 'deliveryCountryCode' | 'orderNumber'>
): UseQueryResult<OrderStatusPanelData> => {
  return useQuery({
    queryKey: [
      'orderStatusPanelData',
      orderId,
      partnerId,
      isDeleted,
      useOverride,
      orderIdHash,
      prod,
      salesOrderSummary
    ],
    queryFn: () =>
      orderStatusPanelQueryFn(
        orderId,
        partnerId,
        isDeleted,
        useOverride,
        orderIdHash,
        prod,
        salesOrderSummary
      ),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

export const useStatusPanelAttachmentsQuery = (
  orderId: string,
  {
    enabled = true,
    refetchOnWindowFocus = false
  }: Partial<QueryProps<AttachmentDto[], {}>> = {}
): UseQueryResult<AttachmentDto[]> => {
  return useQuery({
    queryKey: [
      'statusPanelAttachements',
      orderId,
      ENTITY_TYPE_ID,
      STATUS_IMAGE_ATTACHMENT_TYPE_ID,
      PROOF_OF_DELIVERY_ATTACHMENT_TYPE_ID
    ],
    queryFn: async () => {
      const attachmentTypes = (await getAttachmentTypes()).data;
      const statusImageAttachmentType = attachmentTypes.find(x => x.isStatusImage);
      const proofOfDeliveryAttachmentType = attachmentTypes.find(x => x.isPod);

      const attachments = await getAttachments({
        filter: `entityId eq guid'${orderId}' and (attachmentTypeId eq guid'${
          statusImageAttachmentType?.id ?? ''
        }' or attachmentTypeId eq guid'${
          proofOfDeliveryAttachmentType?.id ?? ''
        }') and entityTypeId eq guid'${ENTITY_TYPE_ID}'`
      });

      return attachments.data;
    },
    enabled: enabled && !!orderId,
    refetchOnWindowFocus
  });
};
